import { ExplorerMigrationOptOut } from '@squareup/dex-feature-dex-tech-main-explorer-page';
import { HeaderContainer } from '@squareup/dex-feature-shared-main-header-container';
import { SearchDomain } from '@squareup/dex-search';
import { useTechRefStore } from '@squareup/dex-store-access-dex-tech-main-store';
import { selectErrorState } from '@squareup/dex-store-access-shared-main-selectors';
import { useSharedStore } from '@squareup/dex-store-access-shared-main-store';
import { loadGraphEntitiesIntoView } from '@squareup/dex-tech-store-access-actions';
import { selectGraphEntitiesInView } from '@squareup/dex-tech-store-access-selectors';
import { NullableClassName } from '@squareup/dex-types-shared-ui';
import { Footer, SuspendOnQueryAction, ThemeSwitcher } from '@squareup/dex-ui';
import { PageLayout } from '@squareup/dex-ui-page-layout';
import { useParams } from '@squareup/dex-utils-shared-routing-hooks';
import React, { PropsWithChildren, useCallback, useMemo } from 'react';

const Header = (props: { searchDomain: SearchDomain } & NullableClassName) => {
  const techRefStore = useTechRefStore();
  const sharedStore = useSharedStore();
  const params = useParams();
  const {
    square_version,
    enum: enumValue,
    object,
    api,
    endpoint,
    webhook,
  } = params ?? ({} as never);

  const query = useMemo(
    () => ({
      square_version: square_version as string,
      enum: enumValue as string,
      object: object as string,
      api: api as string,
      endpoint: endpoint as string,
      webhook: webhook as string,
    }),
    [square_version, enumValue, object, api, endpoint, webhook]
  );

  const loadingSelector = useCallback(
    () => selectGraphEntitiesInView(techRefStore.getState()),
    [techRefStore]
  );

  const errorSelector = useCallback(() => {
    const error = selectErrorState(sharedStore.getState());
    return Boolean(error.global || error.page);
  }, [sharedStore]);

  return (
    <SuspendOnQueryAction
      loadingSelector={loadingSelector}
      action={loadGraphEntitiesIntoView}
      errorSelector={errorSelector}
      query={query}
      apiExplorerQuery={true}
    >
      <HeaderContainer
        className={props.className}
        disableHamburgerMenu
        searchDomain={props.searchDomain}
      />
    </SuspendOnQueryAction>
  );
};

const ContentWrapper = ({ children }: PropsWithChildren<{}>) => {
  const techRefStore = useTechRefStore();
  const sharedStore = useSharedStore();
  const params = useParams();

  const {
    square_version,
    enum: enumValue,
    object,
    api,
    endpoint,
    webhook,
  } = params ?? ({} as never);

  const query = useMemo(
    () => ({
      square_version: square_version as string,
      enum: enumValue as string,
      object: object as string,
      api: api as string,
      endpoint: endpoint as string,
      webhook: webhook as string,
    }),
    [square_version, enumValue, object, api, endpoint, webhook]
  );

  const loadingSelector = useCallback(() => {
    return selectGraphEntitiesInView(techRefStore.getState());
  }, [techRefStore]);

  const errorSelector = useCallback(() => {
    const error = selectErrorState(sharedStore.getState());
    return Boolean(error.global || error.page);
  }, [sharedStore]);

  return (
    <>
      <SuspendOnQueryAction
        loadingSelector={loadingSelector}
        action={loadGraphEntitiesIntoView}
        errorSelector={errorSelector}
        query={query}
        apiExplorerQuery={true}
      >
        {children}
      </SuspendOnQueryAction>
    </>
  );
};

export default function ExplorerBasePage({
  searchDomain = 'apiExplorer',
  children,
}: {
  hideSearch?: boolean;
  searchDomain?: SearchDomain;
  children: React.ReactNode;
}) {
  return (
    // Todo: Use a more specific page layout, similar to how DocsAppPageLayout and DocsContentLayout work
    <PageLayout
      Header={() => <Header searchDomain={searchDomain} />}
      Footer={Footer}
      apiExplorerVariant={true}
      themeSwitcher={<ThemeSwitcher />}
      contents={
        <>
          <ContentWrapper>{children}</ContentWrapper>
          <ExplorerMigrationOptOut />
        </>
      }
    />
  );
}
